import {
  ArrowRight,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Database,
  FolderClosed,
  KeyRound,
  Lock,
  PenLine,
  Shapes,
  ShieldCheck,
  Star,
  TrendingUp,
  Users,
  Wallet,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FacePortrait } from "../components/FacePortrait";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { cn } from "../lib/cn";
import { useTheme } from "../lib/theme";

const GLOW = "radial-gradient(closest-side, rgba(16,118,82,0.28), transparent)";
const SOFT_GLOW = "radial-gradient(closest-side, rgba(16,118,82,0.16), transparent)";
const HATCH =
  "repeating-linear-gradient(-45deg, rgba(255,255,255,0.7) 0, rgba(255,255,255,0.7) 1px, transparent 1px, transparent 9px)";

const FEATURES: { key: string; icon: LucideIcon }[] = [
  { key: "f1", icon: Wallet },
  { key: "f2", icon: PenLine },
  { key: "f3", icon: FolderClosed },
  { key: "f4", icon: CalendarClock },
  { key: "f5", icon: Users },
  { key: "f6", icon: Shapes },
];

const SECURITY: { key: string; icon: LucideIcon }[] = [
  { key: "sec1", icon: Lock },
  { key: "sec2", icon: Building2 },
  { key: "sec3", icon: KeyRound },
  { key: "sec4", icon: Database },
];

// Placeholder agency wordmarks for the social-proof marquee (swap for real logos).
const AGENCIES = [
  "Van der Berg Makelaars",
  "Amstelhaven Hypotheken",
  "De Sleutel Aankoop",
  "Polderhuis Makelaardij",
  "Grachtwerk Advies",
  "Noorderlicht Hypotheken",
  "Stadshuis Makelaars",
  "Maasoever Aankoop",
  "Lindehof Makelaardij",
  "Het Anker Hypotheken",
];

const DATA_SOURCES = ["Kadaster", "CBS", "PDOK", "EP-Online", "Politie"];

// Faces in the hero social-proof stack — a sample of the testimonial people.
const HERO_FACES = ["Sanne de Vries", "Thomas Bakker", "Yasmin El Amrani", "Ravi Pratap"];

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function Landing() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen overflow-x-clip bg-surface-0 font-sans text-ink">
      {/* Apex-domain home: prerendered, so give crawlers a real title/description
          + self-canonical instead of the empty SPA shell. */}
      <Helmet>
        <html lang={t("landing.seoLang")} />
        <title>{t("landing.seoTitle")}</title>
        <meta name="description" content={t("landing.seoDescription")} />
        <link rel="canonical" href="https://biedradar.nl/" />
        <meta property="og:title" content={t("landing.seoTitle")} />
        <meta property="og:description" content={t("landing.seoDescription")} />
        <meta property="og:url" content="https://biedradar.nl/" />
        <meta property="og:type" content="website" />
        {/* PNG, not SVG: WhatsApp/LinkedIn/X refuse SVG link previews. */}
        <meta property="og:image" content="https://biedradar.nl/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://biedradar.nl/og-image.png" />
      </Helmet>
      {/* ── Nav ───────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-line/70 bg-surface-0/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <Link to="/" aria-label="Biedradar Pro">
            <Logo />
          </Link>
          <nav className="flex items-center gap-1">
            <a
              href="#features"
              className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:text-ink md:inline-flex"
            >
              {t("landing.navFeatures")}
            </a>
            <Link
              to="/pricing"
              className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:text-ink md:inline-flex"
            >
              {t("landing.linkPricing")}
            </Link>
            <Link
              to="/blog"
              className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:text-ink md:inline-flex"
            >
              {t("landing.navBlog")}
            </Link>
            <LanguageSwitcher />
            <ThemeToggle />
            <Link
              to="/login"
              className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:text-ink sm:inline-flex"
            >
              {t("landing.signIn")}
            </Link>
            {/* Hidden on phones — it overflows next to the lang/theme controls,
                and the hero CTA is one scroll-less glance below anyway. */}
            <Link
              to="/start?plan=pro"
              className="ring-focus ml-1 hidden items-center whitespace-nowrap gap-1.5 rounded-md bg-brand px-3.5 py-1.5 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-brand-dim sm:inline-flex"
            >
              {t("landing.ctaPrimary")}
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Hero (left-aligned, editorial) ────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-60"
          style={{
            maskImage: "linear-gradient(to bottom, black, transparent 70%)",
            WebkitMaskImage: "linear-gradient(to bottom, black, transparent 70%)",
          }}
        />
        <div
          className="pointer-events-none absolute left-[-10%] top-[-16%] -z-10 h-[60vh] w-[70vw] max-w-3xl rounded-full opacity-60 blur-3xl"
          style={{ background: GLOW }}
        />

        <div className="mx-auto max-w-6xl px-6 pt-20 sm:pt-28 lg:pt-32">
          <div className="max-w-3xl">
            <span className="inline-flex items-center whitespace-nowrap gap-2 rounded-full border border-line bg-surface-2/70 px-3.5 py-1.5 text-xs font-semibold text-ink-muted backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              {t("landing.badge")}
            </span>
            <h1 className="mt-6 font-display text-[clamp(2.75rem,7vw,5.25rem)] font-extrabold leading-[0.97] tracking-[-0.025em] text-ink">
              {t("landing.titleLine1")}{" "}
              <span className="text-brand-soft">{t("landing.titleLine2")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-ink-muted sm:text-xl">
              {t("landing.subtitle")}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/start?plan=pro"
                className="ring-focus group inline-flex items-center whitespace-nowrap justify-center gap-2 rounded-md bg-brand px-6 py-3 text-[15px] font-semibold text-white shadow-glow transition-colors hover:bg-brand-dim"
              >
                {t("landing.ctaPrimary")}
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/login"
                className="ring-focus inline-flex items-center whitespace-nowrap justify-center gap-2 rounded-md border border-line bg-surface-2 px-6 py-3 text-[15px] font-semibold text-ink transition-colors hover:bg-surface-3"
              >
                {t("landing.ctaSecondary")}
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <div className="flex -space-x-2.5">
                {HERO_FACES.map((name) => (
                  <FacePortrait key={name} name={name} size={34} className="border-2 border-surface-0" />
                ))}
              </div>
              <div>
                <Stars size={13} />
                <div className="mt-0.5 text-xs font-medium text-ink-muted">{t("landing.socialProof")}</div>
              </div>
            </div>
          </div>
        </div>

        <HeroCarousel />
      </section>

      {/* ── Logo marquee ──────────────────────────────────────── */}
      <LogoMarquee />

      {/* ── Stats ─────────────────────────────────────────────── */}
      <StatsBand />

      {/* ── Feature: canvas ───────────────────────────────────── */}
      <div id="features" className="scroll-mt-20" />
      <FeatureRow
        eyebrow={t("landing.canvasEyebrow")}
        title={t("landing.canvasTitle")}
        body={t("landing.canvasBody")}
        bullets={[t("landing.canvasB1"), t("landing.canvasB2"), t("landing.canvasB3")]}
        src="/shots/canvas.png"
        alt={t("landing.canvasShotAlt")}
        imageSide="right"
      />

      {/* ── Feature: pipeline ─────────────────────────────────── */}
      <FeatureRow
        eyebrow={t("landing.pipelineEyebrow")}
        title={t("landing.pipelineTitle")}
        body={t("landing.pipelineBody")}
        bullets={[t("landing.pipelineB1"), t("landing.pipelineB2"), t("landing.pipelineB3")]}
        src="/shots/clients.png"
        alt={t("landing.pipelineShotAlt")}
        imageSide="left"
      />

      {/* ── Gallery (more screenshots) ────────────────────────── */}
      <Gallery />

      {/* ── Before / After ────────────────────────────────────── */}
      <BeforeAfter />

      {/* ── Feature grid (numbered) ───────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-[40px] sm:leading-[1.05]">
            {t("landing.featuresTitle")}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-ink-muted">{t("landing.featuresSubtitle")}</p>
        </div>
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <FeatureCard
              key={f.key}
              index={i + 1}
              icon={f.icon}
              title={t(`landing.${f.key}Title`)}
              body={t(`landing.${f.key}Body`)}
            />
          ))}
        </div>
      </section>

      {/* ── The keys moment (photos) ──────────────────────────── */}
      <MomentBand />

      {/* ── Security (dark) ───────────────────────────────────── */}
      <SecuritySection />

      {/* ── Testimonials ──────────────────────────────────────── */}
      <Testimonials />

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <Faq />

      {/* ── Closing CTA ───────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-24 pt-4">
        <div className="relative overflow-hidden rounded-3xl border border-brand-dim/40 bg-gradient-to-br from-brand to-brand-dim px-8 py-16 text-center shadow-pop sm:px-12">
          <div className="pointer-events-none absolute inset-0 opacity-[0.10]" style={{ backgroundImage: HATCH }} />
          <h2 className="relative font-display text-3xl font-extrabold tracking-tight text-white sm:text-[44px] sm:leading-[1.02]">
            {t("landing.ctaTitle")}
          </h2>
          <p className="relative mx-auto mt-4 max-w-xl text-lg leading-relaxed text-white/85">
            {t("landing.ctaBody")}
          </p>
          <Link
            to="/start?plan=pro"
            className="ring-focus relative mt-8 inline-flex items-center whitespace-nowrap gap-2 rounded-md bg-white px-6 py-3 text-[15px] font-semibold text-brand shadow-soft transition-transform hover:-translate-y-0.5"
          >
            {t("landing.ctaButton")}
            <ArrowRight size={17} />
          </Link>
          <p className="relative mt-4 text-sm text-white/70">{t("landing.ctaFootnote")}</p>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <Footer />
    </div>
  );
}

/* ── Sections ───────────────────────────────────────────────── */

function LogoMarquee() {
  const { t } = useTranslation();
  return (
    <section className="border-y border-line bg-surface-1/50 py-9">
      <p className="mb-7 text-center text-xs font-semibold uppercase tracking-[0.18em] text-ink-faint">
        {t("landing.marqueeLabel")}
      </p>
      <div
        className="group relative overflow-hidden"
        style={{
          maskImage: "linear-gradient(to right, transparent, black 7%, black 93%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 7%, black 93%, transparent)",
        }}
      >
        <div className="flex w-max animate-marquee items-center gap-12 pr-12 group-hover:[animation-play-state:paused] motion-reduce:[animation-play-state:paused]">
          {[...AGENCIES, ...AGENCIES].map((name, i) => (
            <div key={i} className="flex shrink-0 items-center gap-2.5">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-ink/[0.05] text-[11px] font-bold text-ink-faint">
                {initials(name)}
              </div>
              <span className="whitespace-nowrap text-[15px] font-semibold tracking-tight text-ink-faint">
                {name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsBand() {
  const { t } = useTranslation();
  const stats = [
    { value: t("landing.stat1Value"), label: t("landing.stat1Label") },
    { value: t("landing.stat2Value"), label: t("landing.stat2Label") },
    { value: t("landing.stat3Value"), label: t("landing.stat3Label") },
    { value: t("landing.stat4Value"), label: t("landing.stat4Label"), stars: true },
  ];
  return (
    <section className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-surface-2 px-6 py-9 text-center">
            <div className="font-display text-[40px] font-extrabold tracking-tight tabular-nums text-ink sm:text-5xl">
              {s.value}
            </div>
            {s.stars && (
              <div className="mt-2 flex justify-center">
                <Stars size={14} />
              </div>
            )}
            <div className="mt-2 text-sm leading-snug text-ink-muted">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const GALLERY: { src: string; capKey: string }[] = [
  { src: "/shots/deal-detail.png", capKey: "galCap1" },
  { src: "/shots/command.png", capKey: "galCap2" },
  { src: "/shots/properties.png", capKey: "galCap3" },
  { src: "/shots/settings.png", capKey: "galCap4" },
];

function Gallery() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto max-w-6xl px-6 py-20 lg:py-24">
      <div className="max-w-2xl">
        <Eyebrow>{t("landing.galleryEyebrow")}</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-[40px] sm:leading-[1.05]">
          {t("landing.galleryTitle")}
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-ink-muted">{t("landing.gallerySubtitle")}</p>
      </div>
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        {GALLERY.map((g) => (
          <figure key={g.src} className="group">
            <div className="overflow-hidden rounded-xl border border-line bg-surface-2 p-1.5 shadow-soft transition-shadow group-hover:shadow-pop">
              <ZoomImage
                src={g.src}
                alt={t(`landing.${g.capKey}`)}
                className="block w-full"
                imgClassName="w-full rounded-lg border border-line/60"
              />
            </div>
            <figcaption className="mt-3 text-sm font-medium text-ink-muted">{t(`landing.${g.capKey}`)}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/** Before / After Biedradar — a blurred-out "chaos" panel (big red X, greyed
 * copy) vs. a crisp €2M+ dashboard (big green check), plus the headline stats.
 * Screenshots resolve light/dark per theme. */
const BA_BEFORE = ["baBefore1", "baBefore2", "baBefore3"] as const;
const BA_AFTER = ["baAfter1", "baAfter2", "baAfter3"] as const;
const BA_BEFORE_STATS = [
  { v: "baBeforeStat1V", l: "baBeforeStat1L" },
  { v: "baBeforeStat2V", l: "baBeforeStat2L" },
  { v: "baBeforeStat3V", l: "baBeforeStat3L" },
] as const;
const BA_AFTER_STATS = [
  { v: "baAfterStat1V", l: "baAfterStat1L" },
  { v: "baAfterStat2V", l: "baAfterStat2L" },
  { v: "baAfterStat3V", l: "baAfterStat3L" },
] as const;

function BeforeAfter() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <section className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
      <div className="mx-auto max-w-2xl text-center">
        <Eyebrow>{t("landing.baEyebrow")}</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-[40px] sm:leading-[1.05]">
          {t("landing.baTitle")}
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-ink-muted">{t("landing.baSubtitle")}</p>
      </div>

      <div className="relative mt-14">
        {/* Brand glow behind the “after” side */}
        <div
          className="pointer-events-none absolute -right-8 top-4 -z-10 hidden h-[88%] w-[55%] rounded-[48px] opacity-70 blur-3xl lg:block"
          style={{ background: "radial-gradient(60% 60% at 70% 30%, rgba(16,118,82,0.22), transparent)" }}
        />

        <div className="grid items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
          {/* BEFORE — chaos */}
          <div className="relative flex flex-col overflow-hidden rounded-2xl border border-line bg-surface-1/70 p-6 sm:p-7">
            <div className="absolute inset-x-0 top-0 h-1 bg-red-500/60" />
            <div className="flex items-center gap-3">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-red-500/10 text-red-500 ring-1 ring-red-500/20">
                <XCircle size={34} strokeWidth={2.25} />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-red-500">{t("landing.baBeforeKicker")}</p>
                <h3 className="font-display text-xl font-bold text-ink-muted">{t("landing.baBeforeLabel")}</h3>
              </div>
            </div>

            <div className="relative mt-6 overflow-hidden rounded-xl border border-line bg-surface-2">
              <img
                src={resolveShot("/shots/before.png", isDark)}
                alt={t("landing.baImgBeforeAlt")}
                loading="lazy"
                className="block aspect-[16/10] w-full object-cover opacity-70 grayscale"
              />
              <div className="pointer-events-none absolute inset-0 bg-red-500/[0.05]" />
              <div className="pointer-events-none absolute inset-0 grid place-items-center">
                <span className="grid h-20 w-20 place-items-center rounded-full bg-surface-0/85 text-red-500 ring-1 ring-red-500/20 backdrop-blur-sm">
                  <X size={44} strokeWidth={2.5} />
                </span>
              </div>
            </div>

            <ul className="mt-6 space-y-3">
              {BA_BEFORE.map((k) => (
                <li key={k} className="flex items-start gap-2.5 text-ink-faint line-through decoration-ink-faint/40">
                  <X size={18} className="mt-0.5 shrink-0 text-red-500/70" />
                  <span>{t(`landing.${k}`)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto grid grid-cols-3 gap-3 border-t border-line pt-6">
              {BA_BEFORE_STATS.map((s) => (
                <div key={s.v}>
                  <div className="font-display text-2xl font-extrabold tracking-tight text-ink-muted">{t(`landing.${s.v}`)}</div>
                  <div className="mt-1 text-xs leading-snug text-ink-faint">{t(`landing.${s.l}`)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* AFTER — in control */}
          <div className="relative flex flex-col overflow-hidden rounded-2xl border-2 border-brand/40 bg-surface-1 p-6 ring-1 ring-brand/10 sm:p-7">
            <div className="absolute inset-x-0 top-0 h-1 bg-brand" />
            <div className="flex items-center gap-3">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/25">
                <CheckCircle2 size={34} strokeWidth={2.25} />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600">{t("landing.baAfterKicker")}</p>
                <h3 className="font-display text-xl font-bold text-ink">{t("landing.baAfterLabel")}</h3>
              </div>
            </div>

            <div className="relative mt-6 overflow-hidden rounded-xl border border-line bg-surface-2">
              <img
                src={resolveShot("/shots/after.png", isDark)}
                alt={t("landing.baImgAfterAlt")}
                loading="lazy"
                className="block aspect-[16/10] w-full object-cover object-top"
              />
              <span className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white">
                <Check size={20} strokeWidth={3} />
              </span>
              {/* Floating proof chips */}
              <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-lg bg-surface-0/90 px-2.5 py-1.5 text-xs font-semibold text-ink ring-1 ring-line backdrop-blur">
                <TrendingUp size={14} className="text-brand" />
                {t("landing.baAfterBadge1")}
              </span>
              <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-lg bg-surface-0/90 px-2.5 py-1.5 text-xs font-semibold text-ink ring-1 ring-line backdrop-blur">
                <Building2 size={14} className="text-brand" />
                {t("landing.baAfterBadge2")}
              </span>
            </div>

            <ul className="mt-6 space-y-3">
              {BA_AFTER.map((k) => (
                <li key={k} className="flex items-start gap-2.5 font-medium text-ink">
                  <Check size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>{t(`landing.${k}`)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto grid grid-cols-3 gap-3 border-t border-brand/20 pt-6">
              {BA_AFTER_STATS.map((s) => (
                <div key={s.v}>
                  <div className="font-display text-2xl font-extrabold tracking-tight text-brand">{t(`landing.${s.v}`)}</div>
                  <div className="mt-1 text-xs leading-snug text-ink-muted">{t(`landing.${s.l}`)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MomentBand() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <Eyebrow>{t("landing.momentEyebrow")}</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-[40px] sm:leading-[1.05]">
            {t("landing.momentTitle")}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-ink-muted">{t("landing.momentBody")}</p>
          <Link
            to="/start?plan=pro"
            className="mt-7 inline-flex items-center whitespace-nowrap gap-1.5 text-[15px] font-semibold text-brand-soft transition-colors hover:text-brand"
          >
            {t("landing.ctaPrimary")}
            <ArrowRight size={16} />
          </Link>
        </div>
        <div className="relative">
          <div
            className="pointer-events-none absolute -inset-6 -z-10 rounded-[32px] opacity-60 blur-3xl"
            style={{ background: SOFT_GLOW }}
          />
          <ZoomImage
            src="/neo-hypotheken-ander-huis-kopen.jpg"
            alt={t("landing.momentPhoto1Alt")}
            className="block"
            imgClassName="aspect-[4/3] w-full rounded-2xl border border-line object-cover shadow-pop"
          />
          <ZoomImage
            src="/neo-hypotheek-overwaarde-opnemen-1.jpg"
            alt={t("landing.momentPhoto3Alt")}
            className="absolute -bottom-8 -left-8 hidden w-2/5 sm:block"
            imgClassName="aspect-square w-full rounded-xl border-4 border-surface-0 object-cover shadow-pop"
          />
          <ZoomImage
            src="/neo-hypotheken-starters-eerste-huis-kopen.jpg"
            alt={t("landing.momentPhoto2Alt")}
            className="absolute -right-6 -top-6 hidden w-1/3 lg:block"
            imgClassName="aspect-square w-full rounded-xl border-4 border-surface-0 object-cover shadow-pop"
          />
        </div>
      </div>
    </section>
  );
}

function SecuritySection() {
  const { t } = useTranslation();
  const policies: { label: string; to: string }[] = [
    { label: t("landing.linkPrivacy"), to: "/privacy" },
    { label: t("landing.linkTerms"), to: "/terms" },
    { label: t("landing.linkSecurity"), to: "/security" },
    { label: t("landing.linkCookies"), to: "/cookies" },
    { label: t("landing.linkDisclaimer"), to: "/disclaimer" },
  ];
  return (
    <section className="mx-auto max-w-6xl px-6 py-12 lg:py-16">
      <div className="relative overflow-hidden rounded-3xl bg-[#0a3a2a] px-7 py-14 sm:px-12 lg:px-14 lg:py-16">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: HATCH }} />
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(45,212,150,0.45), transparent)" }}
        />
        <div className="relative grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <div>
            <span className="inline-flex items-center whitespace-nowrap gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300">
              <ShieldCheck size={14} />
              {t("landing.securityEyebrow")}
            </span>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-[40px] sm:leading-[1.05]">
              {t("landing.securityTitle")}
            </h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-white/70">{t("landing.securityBody")}</p>
            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-white/45">
                {t("landing.securityPoliciesLabel")}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {policies.map((p) => (
                  <Link
                    key={p.label}
                    to={p.to}
                    className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:bg-white/[0.12] hover:text-white"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {SECURITY.map((s) => (
              <div key={s.key} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-white/10">
                  <s.icon size={18} className="text-emerald-300" />
                </div>
                <h3 className="mt-3.5 font-display text-base font-bold text-white">{t(`landing.${s.key}Title`)}</h3>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-white/65">{t(`landing.${s.key}Body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

type Testimonial = { quote: string; name: string; role: string };

function TestimonialCard({ it }: { it: Testimonial }) {
  return (
    <figure className="flex flex-col rounded-2xl border border-line bg-surface-2 p-6 shadow-soft">
      <Stars />
      <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-ink">“{it.quote}”</blockquote>
      <figcaption className="mt-6 flex items-center gap-3 border-t border-line pt-5">
        <FacePortrait name={it.name} size={40} />
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink">{it.name}</div>
          <div className="truncate text-[13px] text-ink-faint">{it.role}</div>
        </div>
      </figcaption>
    </figure>
  );
}

/**
 * A single vertically-scrolling column. The items are rendered twice so the
 * translate-50% loop is seamless; `direction` flips which way it drifts and
 * `duration` lets each column move at its own pace for an organic feel. The
 * track pauses on hover so a reader can stop and actually read a card, and
 * collapses to static (no animation) under `prefers-reduced-motion`.
 */
function MarqueeColumn({
  items,
  direction,
  duration,
  className,
}: {
  items: Testimonial[];
  direction: "up" | "down";
  duration: string;
  className?: string;
}) {
  const anim = direction === "up" ? "animate-marquee-up" : "animate-marquee-down";
  return (
    <div className={className}>
      <div
        className={`flex flex-col gap-5 ${anim} motion-reduce:animate-none [animation-play-state:running] hover:[animation-play-state:paused]`}
        style={{ ["--marquee-duration" as string]: duration }}
      >
        {[...items, ...items].map((it, i) => (
          <TestimonialCard key={`${it.name}-${i}`} it={it} />
        ))}
      </div>
    </div>
  );
}

function Testimonials() {
  const { t } = useTranslation();
  const items: Testimonial[] = Array.from({ length: 18 }, (_, i) => i + 1).map((n) => ({
    quote: t(`landing.t${n}Quote`),
    name: t(`landing.t${n}Name`),
    role: t(`landing.t${n}Role`),
  }));
  // Split into three roughly-even columns; column 2 drifts down, 1 & 3 drift up.
  const col0 = items.filter((_, i) => i % 3 === 0);
  const col1 = items.filter((_, i) => i % 3 === 1);
  const col2 = items.filter((_, i) => i % 3 === 2);

  return (
    <section className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
      <div className="max-w-2xl">
        <Eyebrow>{t("landing.testimonialsEyebrow")}</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-[40px] sm:leading-[1.05]">
          {t("landing.testimonialsTitle")}
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-ink-muted">{t("landing.testimonialsSubtitle")}</p>
      </div>
      <div
        className="relative mt-12 grid h-[560px] grid-cols-1 gap-5 overflow-hidden sm:grid-cols-2 lg:h-[640px] lg:grid-cols-3"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)",
        }}
      >
        <MarqueeColumn items={col0} direction="up" duration="55s" />
        <MarqueeColumn items={col1} direction="down" duration="68s" className="hidden sm:block" />
        <MarqueeColumn items={col2} direction="up" duration="48s" className="hidden lg:block" />
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useTranslation();
  const items = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => ({
    q: t(`landing.q${n}`),
    a: t(`landing.a${n}`),
  }));
  return (
    <section className="mx-auto max-w-3xl px-6 py-20 lg:py-24">
      <div className="text-center">
        <Eyebrow>{t("landing.faqEyebrow")}</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-[40px] sm:leading-[1.05]">
          {t("landing.faqTitle")}
        </h2>
      </div>
      <div className="mt-10 divide-y divide-line border-y border-line">
        {items.map((it) => (
          <details key={it.q} className="group px-1 py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[17px] font-semibold text-ink [&::-webkit-details-marker]:hidden">
              {it.q}
              <ChevronDown
                size={18}
                className="shrink-0 text-ink-faint transition-transform group-open:rotate-180"
              />
            </summary>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-muted">{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-line bg-surface-1/40">
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div className="max-w-xs">
            <Logo />
            <p className="mt-4 text-sm leading-relaxed text-ink-muted">{t("landing.footerTagline")}</p>
            <div className="mt-5 inline-flex items-center whitespace-nowrap gap-2 rounded-md border border-line bg-surface-2 px-2.5 py-1.5 text-xs font-medium text-ink-muted">
              <ShieldCheck size={14} className="text-brand-soft" />
              EU-hosted · GDPR/AVG · encrypted
            </div>
            <p className="mt-4 text-xs leading-relaxed text-ink-faint">{DATA_SOURCES.join(" · ")}</p>
          </div>

          <FooterCol title={t("landing.footerProduct")}>
            <FooterAnchor href="#features">{t("landing.linkFeatures")}</FooterAnchor>
            <FooterLink to="/pricing">{t("landing.linkPricing")}</FooterLink>
            <FooterLink to="/start?plan=pro">{t("landing.ctaPrimary")}</FooterLink>
            <FooterLink to="/login">{t("landing.signIn")}</FooterLink>
          </FooterCol>

          <FooterCol title={t("landing.footerResources")}>
            <FooterLink to="/blog">{t("landing.navBlog")}</FooterLink>
            <FooterLink to="/security">{t("landing.linkSecurity")}</FooterLink>
            <FooterAnchor href="mailto:support@biedradar.nl">{t("landing.linkContact")}</FooterAnchor>
          </FooterCol>

          <FooterCol title={t("landing.footerLegal")}>
            <FooterLink to="/privacy">{t("landing.linkPrivacy")}</FooterLink>
            <FooterLink to="/terms">{t("landing.linkTerms")}</FooterLink>
            <FooterLink to="/cookies">{t("landing.linkCookies")}</FooterLink>
            <FooterLink to="/disclaimer">{t("landing.linkDisclaimer")}</FooterLink>
          </FooterCol>
        </div>
      </div>
      <div className="border-t border-line/70">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-5 text-xs text-ink-faint sm:flex-row">
          <span>
            © {year} Biedradar · {t("landing.footerRights")}
          </span>
          <span className="inline-flex items-center whitespace-nowrap gap-2">
            <span className="inline-flex h-3 w-4 flex-col overflow-hidden rounded-[2px] border border-line">
              <span className="h-1 w-full bg-[#AE1C28]" />
              <span className="h-1 w-full bg-white" />
              <span className="h-1 w-full bg-[#21468B]" />
            </span>
            {t("landing.footerMadeInNl")}
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ── Primitives ─────────────────────────────────────────────── */

/** Click-to-zoom image: opens a full-screen lightbox (Esc / click / × to close). */
function ZoomImage({
  src,
  alt,
  className,
  imgClassName,
  loading = "lazy",
}: {
  src: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  loading?: "lazy" | "eager";
}) {
  const [open, setOpen] = useState(false);
  // Product screenshots ship in light + dark variants (/shots/ and /shots/dark/)
  // so they don't look out of place when the marketing page is in dark mode.
  // Only /shots/ paths have a dark twin — other imagery (photos) is left as-is.
  const { theme } = useTheme();
  const shown =
    theme === "dark" && src.startsWith("/shots/")
      ? src.replace("/shots/", "/shots/dark/")
      : src;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={alt}
        className={cn("block cursor-zoom-in", className)}
      >
        <img src={shown} alt={alt} loading={loading} className={imgClassName} />
      </button>
      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => setOpen(false)}
            className="animate-fade-in fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm sm:p-10"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="ring-focus absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              <X size={20} />
            </button>
            <img
              src={shown}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] w-auto max-w-6xl rounded-lg object-contain shadow-2xl"
            />
          </div>,
          document.body,
        )}
    </>
  );
}

/** Hero carousel — a tour of almost every workspace page. Light/dark variants
 * resolve per theme; arrows + dots page through inline, and clicking opens the
 * full-screen lightbox (X + arrows + keyboard). */
const HERO_SHOTS: { src: string; capKey: string }[] = [
  { src: "/shots/dashboard.png", capKey: "dashboard" },
  { src: "/shots/canvas.png", capKey: "canvas" },
  { src: "/shots/deal-detail.png", capKey: "dealDetail" },
  { src: "/shots/clients.png", capKey: "clients" },
  { src: "/shots/properties.png", capKey: "properties" },
  { src: "/shots/sellers.png", capKey: "sellers" },
  { src: "/shots/team.png", capKey: "team" },
  { src: "/shots/command.png", capKey: "command" },
  { src: "/shots/settings.png", capKey: "settings" },
];

function resolveShot(src: string, isDark: boolean): string {
  return isDark && src.startsWith("/shots/") ? src.replace("/shots/", "/shots/dark/") : src;
}

function HeroCarousel() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [paused, setPaused] = useState(false);
  const n = HERO_SHOTS.length;
  const cur = HERO_SHOTS[i]!;
  const cap = (s: { capKey: string }) => t(`landing.heroShots.${s.capKey}`);
  const step = (d: number) => setI((p) => (p + d + n) % n);

  // Auto-advance the tour every 5s. Keyed on `i`, so manual navigation (arrows,
  // dots) restarts a full window. Pauses while hovered/focused or while the
  // lightbox is open, and sits still for prefers-reduced-motion users.
  useEffect(() => {
    if (paused || zoom) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setTimeout(() => step(1), 5000);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, paused, zoom]);

  return (
    <div
      className="relative mx-auto mt-14 max-w-[78rem] px-6"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div
        className="pointer-events-none absolute -inset-x-4 -top-6 bottom-10 -z-10 rounded-[40px] opacity-70 blur-3xl"
        style={{ background: "radial-gradient(55% 60% at 50% 0%, rgba(16,118,82,0.20), transparent)" }}
      />
      <div className="group relative">
        <div className="overflow-hidden rounded-xl border border-line bg-surface-2 p-1.5 shadow-pop ring-1 ring-ink/[0.03]">
          <button
            type="button"
            onClick={() => setZoom(true)}
            aria-label={`${cap(cur)} — ${t("landing.heroShotZoom")}`}
            className="block w-full cursor-zoom-in"
          >
            <img
              src={resolveShot(cur.src, isDark)}
              alt={cap(cur)}
              loading="eager"
              className="block w-full rounded-lg border border-line/60"
            />
          </button>
        </div>

        {/* Inline prev/next arrows */}
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label={t("landing.heroPrev")}
          className="ring-focus absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-line bg-surface-0/90 text-ink shadow-pop backdrop-blur transition-colors hover:bg-surface-1"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label={t("landing.heroNext")}
          className="ring-focus absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-line bg-surface-0/90 text-ink shadow-pop backdrop-blur transition-colors hover:bg-surface-1"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Caption + dots */}
      <div className="mt-5 flex flex-col items-center gap-3">
        <span className="text-sm font-medium text-ink-soft">{cap(cur)}</span>
        <div className="flex items-center gap-1.5">
          {HERO_SHOTS.map((s, idx) => (
            <button
              key={s.capKey}
              type="button"
              onClick={() => setI(idx)}
              aria-label={cap(s)}
              aria-current={idx === i}
              className={cn(
                "h-1.5 rounded-full transition-all",
                idx === i ? "w-6 bg-brand" : "w-1.5 bg-line hover:bg-ink-faint",
              )}
            />
          ))}
        </div>
      </div>

      {zoom && (
        <Lightbox
          index={i}
          setIndex={setI}
          isDark={isDark}
          caption={cap}
          onClose={() => setZoom(false)}
        />
      )}
    </div>
  );
}

/** Full-screen gallery viewer for the hero shots: X to close, arrows + ←/→ keys
 * to page through, Esc to exit. */
function Lightbox({
  index,
  setIndex,
  isDark,
  caption,
  onClose,
}: {
  index: number;
  setIndex: (updater: (p: number) => number) => void;
  isDark: boolean;
  caption: (s: { capKey: string }) => string;
  onClose: () => void;
}) {
  const n = HERO_SHOTS.length;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setIndex((p) => (p - 1 + n) % n);
      else if (e.key === "ArrowRight") setIndex((p) => (p + 1) % n);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [n, onClose, setIndex]);

  const cur = HERO_SHOTS[index]!;
  const navBtn =
    "ring-focus absolute top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20";

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="animate-fade-in fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/85 p-4 backdrop-blur-sm sm:p-12"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="ring-focus absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
      >
        <X size={20} />
      </button>
      <button
        type="button"
        aria-label="Previous"
        onClick={(e) => {
          e.stopPropagation();
          setIndex((p) => (p - 1 + n) % n);
        }}
        className={cn(navBtn, "left-3 sm:left-6")}
      >
        <ChevronLeft size={24} />
      </button>
      <button
        type="button"
        aria-label="Next"
        onClick={(e) => {
          e.stopPropagation();
          setIndex((p) => (p + 1) % n);
        }}
        className={cn(navBtn, "right-3 sm:right-6")}
      >
        <ChevronRight size={24} />
      </button>
      <img
        src={resolveShot(cur.src, isDark)}
        alt={caption(cur)}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[82vh] w-auto max-w-6xl rounded-lg object-contain shadow-2xl"
      />
      <div className="mt-4 text-sm text-white/80">
        {caption(cur)} · {index + 1}/{n}
      </div>
    </div>,
    document.body,
  );
}

/** Framed product screenshot — mat border, soft edge ring, big shadow. */
function Shot({ src, alt, priority }: { src: string; alt: string; priority?: boolean }) {
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface-2 p-1.5 shadow-pop ring-1 ring-ink/[0.03]">
      <ZoomImage
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        className="block w-full"
        imgClassName="w-full rounded-lg border border-line/60"
      />
    </div>
  );
}

function Stars({ size = 15 }: { size?: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label="5 / 5">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} size={size} className="fill-amber-400 text-amber-400" />
      ))}
    </div>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-soft">{children}</span>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand/12">
        <Check size={13} className="text-brand-soft" strokeWidth={3} />
      </span>
      <span className="text-[15px] leading-relaxed text-ink-muted">{children}</span>
    </li>
  );
}

function FeatureRow({
  eyebrow,
  title,
  body,
  bullets,
  src,
  alt,
  imageSide,
}: {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  src: string;
  alt: string;
  imageSide: "left" | "right";
}) {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div className={cn(imageSide === "left" && "lg:order-2")}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-[40px] sm:leading-[1.05]">
            {title}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-ink-muted">{body}</p>
          <ul className="mt-7 space-y-3.5">
            {bullets.map((b) => (
              <Bullet key={b}>{b}</Bullet>
            ))}
          </ul>
        </div>
        <div className={cn("relative", imageSide === "left" && "lg:order-1")}>
          <div
            className="pointer-events-none absolute -inset-6 -z-10 rounded-[32px] opacity-60 blur-3xl"
            style={{ background: SOFT_GLOW }}
          />
          <Shot src={src} alt={alt} />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  index,
  icon: Icon,
  title,
  body,
}: {
  index: number;
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return (
    <div className="group relative bg-surface-2 p-7 transition-colors hover:bg-surface-1">
      <span className="absolute right-5 top-5 font-display text-sm font-bold tabular-nums text-ink-faint/50">
        {String(index).padStart(2, "0")}
      </span>
      <div className="grid h-11 w-11 place-items-center rounded-lg bg-brand/10">
        <Icon size={20} className="text-brand-soft" />
      </div>
      <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-ink">{title}</h3>
      <p className="mt-2 text-[15px] leading-relaxed text-ink-muted">{body}</p>
    </div>
  );
}

function FooterCol({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-faint">{title}</h3>
      <ul className="mt-4 space-y-2.5 text-sm">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <li>
      <Link to={to} className="text-ink-muted transition-colors hover:text-ink">
        {children}
      </Link>
    </li>
  );
}

function FooterAnchor({ href, children }: { href: string; children: ReactNode }) {
  return (
    <li>
      <a href={href} className="text-ink-muted transition-colors hover:text-ink">
        {children}
      </a>
    </li>
  );
}
